// 開發者審核 API layer(A1;common/08 §3.1/§5)— typed wrappers over the
// LIVE admin endpoints:
//   GET  /v3/admin/dev/applications?state=&cursor=&limit= → data.{items,meta}
//   POST /v3/admin/dev/applications/{user_id}/approve|reject  (reject 需 reason)
//   GET  /v3/admin/dev/reviews → data.models (sparse review-index 佇列)
//   POST /v3/admin/dev/reviews/{m} {action:'approve'|'reject', review_notes}
// 時間戳一律 µs。

import { apiFetch } from '../../lib/api';

export interface DevApplication {
  user_id: string;
  type: string; // personal | org_owner | org_member
  state: string; // applied | approved | rejected | ...（待審 = applied，非 pending）
  slug?: string;
  org_id?: string;
  company_info?: { name?: string };
  rejection_reason?: string;
  applied_at: number; // µs
  reviewed_at?: number;
}

export async function listApplications(state: string): Promise<DevApplication[]> {
  const res = await apiFetch<{ items: DevApplication[] }>('/v3/admin/dev/applications', {
    query: state ? { state } : undefined,
  });
  const data = res.data as unknown as { items?: DevApplication[] };
  return data?.items ?? [];
}

export async function approveApplication(userId: string): Promise<void> {
  await apiFetch(`/v3/admin/dev/applications/${encodeURIComponent(userId)}/approve`, {
    method: 'POST',
    body: {},
  });
}

export async function rejectApplication(userId: string, reason: string): Promise<void> {
  await apiFetch(`/v3/admin/dev/applications/${encodeURIComponent(userId)}/reject`, {
    method: 'POST',
    body: { rejection_reason: reason },
  });
}

export interface ModelReview {
  model: string;
  owner_scope: string;
  display_name: string;
  category?: string;
  state: string;
  review_state: string;
  capability?: { cluster: string; sub?: string[] }[];
  families?: string[];
  created_at: number;
}

export async function listModelReviews(): Promise<ModelReview[]> {
  const res = await apiFetch<{ models: ModelReview[] }>('/v3/admin/dev/reviews');
  const data = res.data as unknown as { models?: ModelReview[] };
  return data?.models ?? [];
}

export async function reviewModel(
  model: string,
  action: 'approve' | 'reject',
  reviewNotes?: string
): Promise<{ state: string; review_state: string }> {
  const res = await apiFetch<{ state: string; review_state: string }>(
    `/v3/admin/dev/reviews/${encodeURIComponent(model)}`,
    { method: 'POST', body: { action, review_notes: reviewNotes ?? '' } }
  );
  return res.data as unknown as { state: string; review_state: string };
}

// ── 開發者帳戶分級(common/08 §3.1.1;LIVE 端點)──
//   GET  /v3/admin/dev/developers?email=            → data (DeveloperAccount)
//   POST /v3/admin/dev/developers/{user_id}/model-quota      {models,reason,case_id}
//   POST /v3/admin/dev/developers/{user_id}/upgrade-enterprise {reason,case_id}

export interface DeveloperAccount {
  user_id: string;
  email?: string;
  type: string; // individual | org | org_member
  state: string;
  account_tier: string; // individual | enterprise
  slug?: string;
  org_id?: string;
  model_quota: number; // effective cap
  quota_override: boolean;
}

export async function lookupDeveloper(email: string): Promise<DeveloperAccount> {
  const res = await apiFetch<DeveloperAccount>('/v3/admin/dev/developers', { query: { email } });
  return res.data as unknown as DeveloperAccount;
}

export async function setModelQuota(userId: string, models: number, reason: string, caseId: string): Promise<void> {
  await apiFetch(`/v3/admin/dev/developers/${encodeURIComponent(userId)}/model-quota`, {
    method: 'POST',
    body: { models, reason, case_id: caseId },
  });
}

export async function upgradeEnterprise(userId: string, reason: string, caseId: string): Promise<void> {
  await apiFetch(`/v3/admin/dev/developers/${encodeURIComponent(userId)}/upgrade-enterprise`, {
    method: 'POST',
    body: { reason, case_id: caseId },
  });
}
